import { doc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../firebase';
import { TriggerSembleMdtReport } from './TriggerSembleMdtReport';

/**
 * Semble Physical Health staleness helpers
 * ----------------------------------------
 * The Semble MDT "Physical Health" paragraph is generated from the raw
 * Clinical Interview Medical History form — panels 8.1 (height, weight, BMI,
 * pulse, blood pressure), 8.2 (health conditions, cardiac symptoms, heart
 * surgery) and 8.4 (current medications), plus each panel's clinicianNotes.
 * When those inputs change after the Semble report was already generated, we
 * must regenerate only that paragraph so the EHR copy stays aligned with what
 * the clinician recorded.
 *
 * This module mirrors python-functions/single_report/semble_mdt_summaries.py
 * (PHYSICAL_HEALTH_RAW_FIELDS + _physical_health_entries) so the frontend
 * dirty-check matches what the Cloud Function will feed the LLM.
 */

/** CI data-model key for the Medical History section. */
export const MEDICAL_HISTORY_SECTION_KEY = '8';

export const SEMBLE_PHYSICAL_HEALTH_CODE = 'semble_physical_health';

// Mirrors PHYSICAL_HEALTH_RAW_FIELDS in semble_mdt_summaries.py.  The question
// labels MUST stay byte-identical to the backend: the dirty-check compares
// stored input strings ("BMI: 24.1") against a freshly-built snapshot, so any
// label drift would make every comparison read dirty forever.
const PHYSICAL_HEALTH_RAW_FIELDS = [
  ['1', 'Physical Health', [
    ['1', 'Height'],
    ['2', 'Weight'],
    ['3', 'BMI'],
    ['4', 'Pulse'],
    ['5', 'Blood pressure'],
  ]],
  ['2', 'Health Conditions', [
    ['1', 'Previous and current health conditions'],
    ['2', 'Cardiac symptoms'],
    ['3', 'History of heart surgery'],
  ]],
  ['4', 'Medications', [
    ['1', 'Current medications'],
  ]],
];

/**
 * Normalise subsection clinicianNotes to a plain string.
 * ADHD CI stores a string; other pathways may use structured arrays.
 */
export function normalizeClinicianNotes(notes) {
  if (typeof notes === 'string') {
    return notes.trim();
  }
  if (Array.isArray(notes)) {
    const parts = [];
    notes.forEach((item) => {
      if (typeof item === 'string' && item.trim()) {
        parts.push(item.trim());
      } else if (item && typeof item === 'object') {
        const text = (item.text || item.value || '').trim();
        if (text) parts.push(text);
      }
    });
    return parts.join('\n');
  }
  return '';
}

/**
 * Build a snapshot of the physical-health inputs that feed the Semble LLM,
 * keyed by node id, with values shaped exactly like the stored review-panel
 * input values so the dirty-check can compare them string-for-string:
 *   - measurements / conditions / medications → "BMI: 24.1" (label-prefixed,
 *     matching the "Patient Prefill" category stored by the backend);
 *   - panel clinician notes → the RAW note text (no "Clinician notes: " prefix,
 *     matching the "Clinician Notes" category — the label already conveys it).
 */
export function buildPhysicalHealthInputSnapshot(medSection) {
  if (!medSection || typeof medSection !== 'object') {
    return {};
  }

  const subsections = medSection.subsections || {};
  const snapshot = {};

  PHYSICAL_HEALTH_RAW_FIELDS.forEach(([subKey, , questions]) => {
    const subsection = subsections[subKey] || {};
    const subQuestions = subsection.subsectionQuestions || {};

    questions.forEach(([qKey, qLabel]) => {
      const question = subQuestions[qKey] || {};
      const value = question.patientPrefill;
      if (typeof value === 'string' && value.trim()) {
        const nodeId = question.nodeId || `2_8_${subKey}_${qKey}`;
        snapshot[nodeId] = `${qLabel}: ${value.trim()}`;
      }
    });

    const notesText = normalizeClinicianNotes(subsection.clinicianNotes);
    if (notesText) {
      snapshot[`2_8_${subKey}_notes`] = notesText;
    }
  });

  return snapshot;
}

/**
 * Compare the live Medical History section against the inputs stored on the
 * last Semble generation.  Returns true when any measurement, condition, or
 * panel note differs.
 */
export function isPhysicalHealthInputsDirty(storedInputs, medSection) {
  const stored = storedInputs && typeof storedInputs === 'object' ? storedInputs : {};
  const current = buildPhysicalHealthInputSnapshot(medSection);
  const allKeys = new Set([...Object.keys(stored), ...Object.keys(current)]);

  for (const key of allKeys) {
    const storedVal = (stored[key]?.value || '').trim();
    const currentVal = (current[key] || '').trim();
    if (storedVal !== currentVal) {
      return true;
    }
  }
  return false;
}

/** Read the CI Medical History section from the patient data model. */
export function getMedicalHistorySection(patientDataModel) {
  return patientDataModel?.dataModel?.['2_clinicalInterview']
    ?.sections?.[MEDICAL_HISTORY_SECTION_KEY];
}

/**
 * If the Semble report exists and physical-health inputs are stale, clear
 * that paragraph's approval (dashboard badge) and trigger targeted CF
 * regeneration.  Fire-and-forget — callers should not block navigation.
 *
 * @param {object} [params.medSectionOverride] - Optional in-memory section
 *   snapshot when Firestore/context has not caught up yet (e.g. notes blur).
 */
export function triggerSemblePhysicalHealthRegenIfDirty({
  patientId,
  clinicianId,
  patientDataModel,
  isSembleMdtReportEnabled,
  medSectionOverride,
}) {
  if (!isSembleMdtReportEnabled || !patientId || !clinicianId) {
    return;
  }

  const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
  if (!manageReports?.sembleMDTReportGenerated) {
    return;
  }

  const medSection = medSectionOverride || getMedicalHistorySection(patientDataModel);
  // Physical-health inputs are stored across two categories: patient-prefilled
  // values ("Patient Prefill") and panel clinician notes ("Clinician Notes").
  // Merge both before comparing.  Their keys are disjoint (node ids vs
  // "2_8_{n}_notes"), so the merge is lossless, and it also tolerates legacy
  // reports that stored everything under "Clinician Notes" (those self-heal to
  // the split format on the next regeneration).
  const storedForCode = manageReports?.sections?.['10']?.inputs?.[SEMBLE_PHYSICAL_HEALTH_CODE];
  const storedInputs = {
    ...(storedForCode?.['Patient Prefill'] || {}),
    ...(storedForCode?.['Clinician Notes'] || {}),
  };

  if (!isPhysicalHealthInputsDirty(storedInputs, medSection)) {
    return;
  }

  // Optimistically clear approval so the dashboard shows "ready for review"
  // while the CF runs — same pattern as MDT Plan goals/treatment notes.
  updateDoc(doc(db, 'patients', patientId), {
    [`dataModel.6_manageReports.sections.10.approvedParagraphs.${SEMBLE_PHYSICAL_HEALTH_CODE}`]: deleteField(),
    'dataModel.6_manageReports.sembleMDTReportApproved': false,
    'dataModel.6_manageReports.sembleMDTReportApprovedBy': null,
    'dataModel.6_manageReports.sembleMDTReportApprovedByName': null,
    'dataModel.6_manageReports.sembleMDTReportApprovedAt': null,
  }).catch((error) => {
    console.error('Failed to reset Semble physical health staleness flags:', error);
  });

  TriggerSembleMdtReport({
    clinicianId,
    patientId,
    forceRegenerateKeys: [SEMBLE_PHYSICAL_HEALTH_CODE],
    waitAndRetry: true,
  }).catch((error) => {
    console.error('triggerSemblePhysicalHealthRegenIfDirty failed:', error);
  });
}

/**
 * Build an optimistic Medical History section by overlaying in-memory panel
 * notes onto the Firestore-backed section (notes state can be ahead of context).
 */
export function buildOptimisticMedicalHistorySection(patientDataModel, panelNotesByIndex) {
  const base = getMedicalHistorySection(patientDataModel) || {};
  const subsections = { ...(base.subsections || {}) };

  Object.entries(panelNotesByIndex || {}).forEach(([panelIndex, noteText]) => {
    const subKey = String(Number(panelIndex) + 1);
    subsections[subKey] = {
      ...(subsections[subKey] || {}),
      clinicianNotes: noteText,
    };
  });

  return { ...base, subsections };
}
